import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 px-4 md:px-6 lg:px-8 bg-[#F2F0E9]"
      data-testid="contact-section"
    >
      <div className="container mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-block p-4 bg-[#2E5C44] rounded-full mb-6">
            <Mail className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1A2D23] mb-4">
            Get In Touch
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[#5C6B63]">
            Have a question or want to explore partnership opportunities? We'd
            love to hear from you.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white border border-[#E2E0D6] p-8 rounded-2xl shadow-lg space-y-6"
          data-testid="contact-form"
        >
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-[#1A2D23] mb-2"
            >
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-white border border-[#E2E0D6] focus:border-[#2E5C44] focus:ring-2 focus:ring-[#2E5C44] rounded-lg h-12 px-4 transition-colors duration-300"
              data-testid="contact-name-input"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-[#1A2D23] mb-2"
            >
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full bg-white border border-[#E2E0D6] focus:border-[#2E5C44] focus:ring-2 focus:ring-[#2E5C44] rounded-lg h-12 px-4 transition-colors duration-300"
              data-testid="contact-email-input"
            />
          </div>

          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-[#1A2D23] mb-2"
            >
              Subject *
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full bg-white border border-[#E2E0D6] focus:border-[#2E5C44] focus:ring-2 focus:ring-[#2E5C44] rounded-lg h-12 px-4 transition-colors duration-300"
              data-testid="contact-subject-input"
            />
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-[#1A2D23] mb-2"
            >
              Message *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full bg-white border border-[#E2E0D6] focus:border-[#2E5C44] focus:ring-2 focus:ring-[#2E5C44] rounded-lg p-4 resize-none transition-colors duration-300"
              data-testid="contact-message-input"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-[#2E5C44] text-white hover:bg-[#234734] px-8 py-4 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit-btn"
          >
            {isSubmitting ? (
              'Sending...'
            ) : (
              <>
                Send Message
                <Send className="w-5 h-5" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;